/**
 * Scout's runs and omnium's runs on one timeline, lined up where omnium read a
 * Scout run — so "Scout passed but omnium did not read it" shows as its own
 * state instead of a blank cell in a second table.
 */

import type { Run, ScoutRunJoined } from "@/lib/integrations/api";

/**
 * - `read`: omnium read this passed run.
 * - `not_read`: it passed, and it is newer than anything omnium read.
 * - `older`: it passed, but omnium already holds a newer one.
 * - `nothing`: Scout's run did not pass, so there was nothing to read.
 * - `no_scout`: omnium's own runs of a Scout run that is not in Scout's recent list.
 */
export type ReadState = "read" | "not_read" | "older" | "nothing" | "no_scout";

export type TimelineEntry = { key: string; at: string | null; scout: ScoutRunJoined | null; ours: Run[]; read: ReadState };

function time(moment: string | null | undefined): number {
  const at = moment ? Date.parse(moment) : Number.NaN;
  return Number.isFinite(at) ? at : Number.NEGATIVE_INFINITY;
}

function newestFirst(runs: Run[]): Run[] {
  return [...runs].sort((a, b) => time(b.createdAt) - time(a.createdAt));
}

const real = (run: Run) => run.via !== "try";

export function lineUpRuns(ours: Run[], scout: ScoutRunJoined[]): TimelineEntry[] {
  const used = new Set<string>();
  const entries: TimelineEntry[] = scout.map((one) => {
    const mine = newestFirst(ours.filter((run) => run.scoutRunId === one.id || one.omnium?.runId === run.id));
    for (const run of mine) used.add(run.id);
    return { key: `scout:${one.id}`, at: one.startedAt ?? mine[mine.length - 1]?.createdAt ?? null, scout: one, ours: mine, read: "nothing" };
  });

  const loose = new Map<string, Run[]>();
  for (const run of ours) {
    if (used.has(run.id)) continue;
    const key = run.scoutRunId ?? run.id;
    loose.set(key, [...(loose.get(key) ?? []), run]);
  }
  for (const [key, runs] of loose) {
    const sorted = newestFirst(runs);
    entries.push({ key: `ours:${key}`, at: sorted[0].scoutStartedAt ?? sorted[0].createdAt, scout: null, ours: sorted, read: "no_scout" });
  }

  const newestRead = Math.max(
    Number.NEGATIVE_INFINITY,
    ...entries.filter((entry) => entry.scout && entry.ours.some(real)).map((entry) => time(entry.at)),
  );
  for (const entry of entries) {
    if (!entry.scout) continue;
    if (entry.scout.status.toLowerCase() !== "passed") entry.read = "nothing";
    else if (entry.ours.some(real)) entry.read = "read";
    else entry.read = time(entry.at) > newestRead ? "not_read" : "older";
  }

  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => time(b.entry.at) - time(a.entry.at) || a.index - b.index)
    .map(({ entry }) => entry);
}
